import { Box, Typography, Tooltip, useTheme, Card, Button, } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { observer } from "mobx-react"

import SubmitAssignmentDialog from "../../components/user/SubmitAssignmentDialog"
import DataLoadingSpinner from "../../components/common/DataLoadingSpinner"
import AssignmentViewer from "../../components/common/AssignmentViewer"
import CustomSearchBar from "../../components/common/CustomSearchBar"
import { useAssignmentStore } from "../../store/assignment.store"
import noAssignmentImg from '../../assets/noassignment.svg'
import CustomTabs from '../../components/common/CustomTabs'
import CountCard from '../../components/common/CountCard'
import { ASSIGNMENT_CONVERT_DUE_TIME_BEFORE, Icons, formatDate, getTimePassedFrom } from "../../utils/utilities"
import dayjs from 'dayjs'
import CustomChip from '../../components/common/CustomChip'

const tabs = [
    { label: "Due", value: "due", icon: Icons.default.Checkbox },
    { label: "Completed", value: "completed", icon: Icons.default.CheckboxChecked },
]

const Assignment = observer( () => {

    const AssignmentStore = useAssignmentStore()

    const [loaded, setLoaded] = useState( false )

    const [openAssignmentSubmission, setOpenAssignmentSubmission] = useState( false )

    const [selectedValue, setSelectedValue] = useState( {} )
    const [selectedAssignmentData, setSelectedAssignmentData] = useState( {} )

    const [searchParams, setSearchParams] = useSearchParams( { open: false, selectedAssignmentId: "", assignmentFilter: "due", searchText: "" } )
    const assignmentFilter = searchParams.get( 'assignmentFilter' ) || "due"
    const selectedAssignmentId = searchParams.get( 'selectedAssignmentId' ) || ""
    const searchText = searchParams.get( 'searchText' ) || ""
    const open = searchParams.get( 'open' ) === 'true'

    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const { palette, border, shadows, layout } = useTheme()

    const getAssignments = async () => {
        if ( AssignmentStore.getErrors.assignments )
            await AssignmentStore.fetchAssignmentByStudent()
        setLoaded( true )
    }

    useEffect( () => {
        const getAssignments = async () => {
            if ( AssignmentStore.getErrors.assignments )
                await AssignmentStore.fetchAssignmentByStudent()

            if ( selectedAssignmentId && Number.isInteger( parseInt( selectedAssignmentId ) ) ) {
                setSelectedValue( AssignmentStore.getAssignment.find( assignment => +assignment.assignment_id === +selectedAssignmentId ) )
                setSearchParams( prev => {
                    const newVals = new URLSearchParams( prev )
                    newVals.set( 'open', true )
                    return newVals
                }, { replace: true } )
            } else {
                setSearchParams( prev => {
                    const newVals = new URLSearchParams( prev )
                    newVals.set( 'open', false )
                    return newVals
                }, { replace: true } )
            }
            setLoaded( true )
        }
        getAssignments()
    }, [AssignmentStore, setSearchParams, selectedAssignmentId] )

    const handleClickOpen = ( value ) => {
        setSelectedValue( value )
        setSearchParams( prev => {
            const newVals = new URLSearchParams( prev )
            newVals.set( 'open', false )
            newVals.set( 'selectedAssignmentId', value.assignment_id )
            return newVals
        }, { replace: true } )
    }

    const handleClose = ( value ) => {
        setSearchParams( prev => {
            const newVals = new URLSearchParams( prev )
            newVals.set( 'open', false )
            newVals.set( 'selectedAssignmentId', '' )
            return newVals
        }, { replace: true } )
        setSelectedValue( value )
    }

    const handleClickOpenAssignmentSubmission = ( value ) => {
        setSelectedAssignmentData( value )
        setOpenAssignmentSubmission( true )
    }

    const handleCloseAssignmentSubmission = ( value ) => {
        setOpenAssignmentSubmission( false )
        setSelectedAssignmentData( value )
    }

    const assignmentFilterFunc = ( item ) => {
        if ( assignmentFilter === "all" ) return true && item["assignment_name"]?.toLowerCase()?.indexOf( searchText?.toLowerCase() ) > -1
        if ( assignmentFilter === "due" ) {
            let dueDate = dayjs( item.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
                dayjs( dayjs( item.assignment_due_date ).format( "YYYY-MM-DD" ) ).add( 1, 'day' ) :
                dayjs( item.assignment_due_date )
            return +item.submission_exists === 0 && dueDate > dayjs() &&
                item["assignment_name"]?.toLowerCase()?.indexOf( searchText?.toLowerCase() ) > -1
        }
        if ( assignmentFilter === "completed" ) {
            let dueDate = dayjs( item.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
                dayjs( dayjs( item.assignment_due_date ).format( "YYYY-MM-DD" ) ).add( 1, 'day' ) :
                dayjs( item.assignment_due_date )
            return ( item.submission_exists > 0 || dueDate < dayjs() )
                && item["assignment_name"]?.toLowerCase()?.indexOf( searchText?.toLowerCase() ) > -1
        }
        return true
    }

    const dueAssignments = AssignmentStore.getAssignment.filter( item => {
        let dueDate = new Date( item.assignment_due_date )
        dueDate = new Date( dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate() + 1 )
        return +item.submission_exists === 0 && dueDate > new Date() &&
            item["assignment_name"]?.toLowerCase()?.indexOf( searchText?.toLowerCase() ) > -1
    } )


    return (
        <Box padding={layout.pagePadding} gap={layout.sectionsGap} display="flex" flexDirection="column" flexGrow={1} overflow="auto" minWidth="0" className="main">

            {/* DIALOGS */}
            <AssignmentViewer selectedValue={selectedValue} open={open} loaded={loaded} onClose={handleClose} />
            <SubmitAssignmentDialog getAssignments={getAssignments} selectedValue={selectedAssignmentData} open={openAssignmentSubmission} onClose={handleCloseAssignmentSubmission} />

            <Box display="flex" gap="20px" flexDirection="column">
                <Box >
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Assignments</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>View all your assignments here</Typography>
                </Box>

                {loaded && <Box display="grid" gap="20px" width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                    <CountCard label='Total Assignments' icon={Icons.default.DescriptionIcon} value={AssignmentStore.getAssignment.length} />
                    <CountCard label='Active Assignments' icon={Icons.default.FiberManualRecordIcon} value={dueAssignments.length} />
                </Box>}

                {( AssignmentStore.getAssignment.length !== 0 && loaded ) &&
                    <Box>
                        {loaded && ( AssignmentStore?.getAssignment || [] ).length > 0 && <Box display="flex" flexWrap="wrap" gap="10px" justifyContent="space-between">

                            <CustomTabs tabs={tabs} value={assignmentFilter} handleTabChange={newVal => setSearchParamValue( 'assignmentFilter', newVal )} />

                            <CustomSearchBar border={border[1]} style={{ flexGrow: 1 }} value={searchText} onChange={e => {
                                setSearchParamValue( 'searchText', e.target.value )
                            }} placeholder='Search assignments...' />
                        </Box>}
                        <Box display="grid" gridTemplateColumns={{ md: "repeat(3,1fr)", sm: "repeat(2,1fr)", xs: "1fr" }} marginTop="20px" flexWrap="wrap" gap="20px">
                            {
                                loaded && AssignmentStore.getAssignment.filter( assignmentFilterFunc ).map( ( assignment, k ) => {
                                    let dueDate = dayjs( assignment.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
                                        dayjs( dayjs( assignment.assignment_due_date ).format( "YYYY-MM-DD" ) ).add( 1, 'day' ) :
                                        dayjs( assignment.assignment_due_date )
                                    const now = new Date()
                                    const isUrgent = dueDate.diff( dayjs() ) < 24 * 60 * 60 * 1000


                                    return (
                                        <Card sx={{ border: border[1], ":hover": { boxShadow: shadows[2] } }} key={assignment.assignment_id}>
                                            <Box borderRadius="5px" sx={{ "&:hover": { boxShadow: `1px 0 5px 1px rgba(0,0,0,0.1)` } }} overflow="hidden">
                                                {+assignment["submission_exists"] === 0 && <Box gap="10px" fontSize="28px" margin="10px" flexGrow={1} border={`1px solid ${palette[isUrgent ? "error" : "primary"].main}`} borderRadius="5px" padding="5px 10px" justifyContent="flex-start" display="flex" alignItems="center" bgcolor={palette[isUrgent ? "error" : "primary"].light + "11"} >
                                                    {Icons.default.Calendar}
                                                    <Box>
                                                        <Typography component="strong" display="block" color='textSecondary' fontSize="12px"  >Due on</Typography>
                                                        <Typography marginTop="-2px" component="strong" display="block" color='testPrimary' fontSize="12px"  >{
                                                            dayjs( assignment.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
                                                                `${dayjs( assignment.assignment_due_date ).format( "DD MMM YYYY" )}, 11:59 PM` :
                                                                dayjs( assignment.assignment_due_date ).format( "DD MMM YYYY, hh:mm A" )
                                                        }</Typography>
                                                    </Box>
                                                </Box>}
                                                {+assignment["submission_exists"] !== 0 && <Box gap="10px" fontSize="28px" margin="10px" flexGrow={1} border={`1px solid ${palette.success.main}`} borderRadius="5px" padding="5px 10px" justifyContent="flex-start" display="flex" alignItems="center" bgcolor={palette.success.light + "11"} >
                                                    {Icons.default.AnalyticsIcon}
                                                    <Box>
                                                        <Typography component="strong" display="block" color='textSecondary' fontSize="12px"  >Submitted</Typography>
                                                        <Typography marginTop="-2px" component="strong" display="block" color='testPrimary' fontSize="12px"  >Score: {assignment["assignment_marks"] || "Not assigned"}</Typography>
                                                    </Box>
                                                </Box>}
                                                {/* <Typography color={palette.common.bg} sx={{ stroke: "red" }} >{Icons.small.Subject}</Typography> */}
                                                <Box onClick={() => handleClickOpen( assignment )} flexGrow={1} sx={{ cursor: "pointer" }} paddingX={{ sm: "10px", xs: "10px" }} overflow="hidden">
                                                    <Tooltip placement='top' disableInteractive title={assignment.assignment_name} >
                                                        <Typography variant='h6' component="p" fontWeight="500" width="100%" noWrap fontSize="16px">{assignment.assignment_name}</Typography>
                                                    </Tooltip>
                                                    {/* {assignment?.assignment_subject && <Tooltip disableInteractive placement='bottom-start' title="Subject Code" >
                                                        <Typography variant='body1' component="p" width="fit-content" border={border[1]} padding="2px 5px" borderRadius="10px" noWrap fontSize="10px">{`${assignment?.assignment_subject?.subject_code}`}</Typography>
                                                        </Tooltip>} */}
                                                    <Typography display="flex" alignItems="center" gap="5px" noWrap fontSize="12px" > {Icons.default.User} {assignment.author_name}</Typography>
                                                    <Typography display="flex" alignItems="center" gap="5px" noWrap fontSize="12px" > {Icons.default.Subjects} {assignment?.assignment_subject?.subject_name}</Typography>
                                                    <Box display="flex" marginTop="5px" alignItems="center" flexWrap="wrap" gap="5px">
                                                        <CustomChip
                                                            color="greyed"
                                                            tooltipTitle={assignment.assignment_submission_mode === 'offline' ? "Submitted through yellow books" : "Submitted through Alive platform"}
                                                            startIcon={assignment.assignment_submission_mode === 'offline' ? Icons.default.Class : Icons.default.SystemTheme}
                                                            value={assignment.assignment_submission_mode || 'online'}
                                                        />
                                                        <CustomChip color="greyed" startIcon={Icons.default.Calendar} tooltipTitle={`Created at: ${formatDate( assignment.assignment_created_at, { format: "LLL" }, dayjs( assignment.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ? { hours: 5, minutes: 30 } : 0 )}`} value={`Created ${getTimePassedFrom( assignment.assignment_created_at )}`} />
                                                    </Box>
                                                </Box>
                                                <Box padding="10px" display="flex" flexWrap="wrap" gap="5px" justifyContent="space-between" >

                                                    <Box display="flex" flexGrow={1}>
                                                        <Tooltip sx={{ flexGrow: 1 }} title={assignment.assignment_submission_mode === "offline" ? "Submit this offline assignment via the yellow book or as otherwise instructed by your faculty." : +assignment["submission_exists"] !== 0 ? "Already submitted" : now > dueDate ? "Due date passed" : "" ? "Already submitted" : "Upload assignment"}>
                                                            <Box sx={{ flexGrow: 1, cursor: assignment.assignment_submission_mode === "offline" ? "not-allowed" : "pointer" }} display="flex" >
                                                                <Button fullWidth variant="contained" disableElevation sx={{ textTransform: "capitalize" }} startIcon={Icons.default.UploadFileIcon} disabled={assignment["submission_exists"] !== "0" || assignment.assignment_submission_mode === "offline" || now > dueDate} onClick={( e ) => handleClickOpenAssignmentSubmission( assignment )} >
                                                                    Upload
                                                                </Button>
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                                {assignmentFilter !== "due" && <Box borderTop={border[1]} padding="10px 20px">
                                                    <Typography component="em" title={assignment["assignment_remarks"] ? `Remarks: ${assignment["assignment_remarks"]}` : "No remarks"} display="block" noWrap fontSize="12px" >{assignment["assignment_remarks"] ? `Remarks: ${assignment["assignment_remarks"]}` : "No remarks"}</Typography>
                                                </Box>}
                                            </Box>
                                        </Card>

                                    )
                                } )
                            }
                        </Box>
                        {loaded && AssignmentStore.getAssignment.filter( assignmentFilterFunc ).length === 0 && <Box borderRadius="5px" padding={{ sm: "20px", xs: "10px" }} display="flex" alignItems="center" justifyContent="center" flexGrow={1} flexDirection="column" bgcolor={palette.contentBg}>
                            <img src={noAssignmentImg} width="200px" className="img-fluid" alt="No Assignments Just Relax" />
                            <Typography variant="subtitle2" color="textSecondary" marginTop="10px">No Assignment in <strong><em>{assignmentFilter}</em></strong> section
                            </Typography>
                        </Box>}
                    </Box>}
                {
                    !loaded && <DataLoadingSpinner padding='0' waitingMessage="Loading assignments..." />
                }
            </Box>
            {
                ( ( AssignmentStore.getAssignment.length === 0 && loaded ) ? <Box borderRadius="5px" padding={{ sm: "20px", xs: "10px" }} display="flex" alignItems="center" justifyContent="center" flexDirection="column" bgcolor={palette.contentBg}>
                    <img src={noAssignmentImg} width="200px" className="img-fluid" alt="No Assignments Just Relax" />
                    <Typography variant="subtitle2" color="textSecondary" marginTop="10px">No Assignment Just Relax!
                    </Typography>
                </Box> : '' )
            }
        </Box>
    )
} )



export default Assignment